import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import MuiIconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import BoltIcon from '@mui/icons-material/Bolt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EmailOutlinedIcon from '@mui/icons-material/MailOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import SentimentSatisfiedOutlinedIcon from '@mui/icons-material/SentimentSatisfiedOutlined';
import { convertDateToStringFormat, formatMonthYearFr } from '@bloomays-lib/utils.shared';
import { CRADashboardItemGQL, Maybe } from '@bloomays-lib/types.shared';
import {
  CardItem,
  ObjectiveKpiCard,
  TitlePart,
  SingleLineText,
  LoaderGlobal,
  Modal,
  FileViewer,
  DatePicker,
  DatePickerValue,
} from '@bloomays-lib/ui.shared';
import { DataGridV2, StatusChip, formatEuro } from '@bloomays-lib/ui.shared/datagrid';
import type {
  DataGridV2Column,
  DataGridV2LegendItem,
  DataGridV2QuickFilter,
  DataGridV2Tab,
} from '@bloomays-lib/ui.shared/datagrid';
import {
  CRA_CHIP,
  CraDashboardRow,
  CraRowEnrichment,
  FACT_CHIP,
  FactState,
  SIG_CHIP,
  buildAmOptions,
  canForceCRA,
  canRelaunchBloomer,
  canRelaunchClient,
  canRetryBloomerInvoice,
  canRetryClientInvoice,
  canUploadAdditionalInvoice,
  canUploadBloomerInvoice,
  computeCraKpis,
  hasAdditionalInvoice,
  craStatusOptions,
  deriveCraRow,
  factStatusOptions,
  isAlert,
  isInProgress,
  isValidated,
  needsCraValidation,
  needsInvoiceAction,
  npsBucketOptions,
} from '../../helpers/craDashboard';
import CRADetailPanel from '../molecules/CRADetailPanel';
import { useAuthStore } from '../../stores';
import { PageHeader } from '../molecules/PageHeader';

/** Controlled quick-filter that the KPI cards drive (cross-cutting action views). */
const FOCUS_FIELD = 'focus';

export type ActivityDashboardV2Props = {
  rows: CRADashboardItemGQL[];
  loading: boolean;
  /** `true` when the dataset is scoped to a single mission (getCraByMission). */
  missionMode: boolean;
  currentMonth: Date;
  /** Optional enrichment (montants HT facturés) the CRA payload doesn't carry. */
  enrich?: (item: CRADashboardItemGQL) => CraRowEnrichment;
  /** Switch month (month mode only) — wires the header picker / arrows. */
  onMonthChange?: (month: Date) => void;
  onForceCRA?: (row: CraDashboardRow) => void;
  onRelaunchClient?: (row: CraDashboardRow) => void;
  onRelaunchBloomer?: (row: CraDashboardRow) => void;
  onRetryClientInvoice?: (row: CraDashboardRow) => void;
  onRetryBloomerInvoice?: (row: CraDashboardRow) => void;
  onUploadBloomerInvoice?: (row: CraDashboardRow) => void;
  onUploadAdditionalInvoice?: (row: CraDashboardRow) => void;
  onViewHistory?: (row: CraDashboardRow) => void;
  onBulkRelaunch?: (rows: CraDashboardRow[]) => void;
  onExportSelection?: (rows: CraDashboardRow[]) => void;
};

const initials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

/** Stable detail-panel selection key — one CRA per mission & month. */
const selectionKey = (row: CraDashboardRow): string =>
  `${row.missionId}|${row.activityMonth ? new Date(row.activityMonth).toISOString().slice(0, 7) : ''}`;

const fmtPct = (pct: number) => `${Math.round(pct * 100)} %`;
/** Round day totals to at most 1 decimal (half-days) — avoids float noise. */
const fmtDays = (n: number) => `${Math.round(n * 10) / 10}`;
const fmtMonthLong = (d: Date) => formatMonthYearFr(d);
const fmtMonthShort = (d?: Date | null) => (d ? convertDateToStringFormat(new Date(d), 'MM/yyyy') : '—');

const ActivityDashboardV2 = ({
  rows: rawRows,
  loading,
  missionMode,
  currentMonth,
  enrich,
  onMonthChange,
  onForceCRA,
  onRelaunchClient,
  onRelaunchBloomer,
  onRetryClientInvoice,
  onRetryBloomerInvoice,
  onUploadBloomerInvoice,
  onUploadAdditionalInvoice,
  onViewHistory,
  onBulkRelaunch,
  onExportSelection,
}: ActivityDashboardV2Props) => {
  // Detail panel selection. We keep a *stable key* (mission + month) rather than
  // a row snapshot, so the panel always reflects the live row — it stays in sync
  // after an action triggers an optimistic update / refetch (the activityId, and
  // thus rowId, can even change when forcing a not-yet-created CRA).
  const [selectedKey, setSelectedKey] = useState<string | undefined>();
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<{ url: string; title: string } | undefined>();

  // Access token for the secured file preview (CRA / Bloomer invoice), like the
  // legacy ActivityDashboard.
  const auth = useAuthStore((state) => state.auth);
  const [token, setToken] = useState<string | undefined>();
  useEffect(() => {
    if (auth?.getAccessTokenSilently) {
      void auth.getAccessTokenSilently().then(setToken);
    }
  }, [auth]);

  const rows = useMemo<CraDashboardRow[]>(
    () => rawRows.map((item) => deriveCraRow(item, enrich?.(item))),
    [rawRows, enrich],
  );

  const selected = useMemo(
    () => (selectedKey ? rows.find((r) => selectionKey(r) === selectedKey) : undefined),
    [rows, selectedKey],
  );

  const kpis = useMemo(() => computeCraKpis(rows), [rows]);
  const amOptions = useMemo(() => buildAmOptions(rows), [rows]);

  const relaunchEligibleBloomers = useMemo(() => rows.filter(canRelaunchBloomer), [rows]);
  const relaunchEligibleClients = useMemo(() => rows.filter(canRelaunchClient), [rows]);

  const periodLabel = missionMode ? rows[0]?.mission || 'Mission' : fmtMonthLong(currentMonth);

  // KPI cards toggle the cross-cutting `focus` quick filter.
  const focusValue = filterValues[FOCUS_FIELD] ?? '';
  const toggleFocus = (value: string) =>
    setFilterValues((prev) => ({ ...prev, [FOCUS_FIELD]: prev[FOCUS_FIELD] === value ? '' : value }));

  const tabs: DataGridV2Tab<CraDashboardRow>[] = [
    { key: 'tous', label: 'Tous' },
    { key: 'alertes', label: '🔴 Alertes', predicate: isAlert },
    { key: 'en-cours', label: '⏳ En cours', predicate: isInProgress },
    { key: 'valides', label: '✓ Validés', predicate: isValidated },
  ];

  const quickFilters: DataGridV2QuickFilter<CraDashboardRow>[] = [
    {
      field: FOCUS_FIELD,
      label: 'À traiter',
      display: 'inline',
      allLabel: 'Tout',
      options: [
        { value: 'cra', label: 'CRA non validés', tone: 'warning' },
        { value: 'facture', label: 'Factures à traiter', tone: 'danger' },
        { value: 'add', label: 'Avec facture add.', tone: 'info' },
      ],
      predicate: (row, value) =>
        value === 'cra'
          ? needsCraValidation(row)
          : value === 'facture'
          ? needsInvoiceAction(row)
          : value === 'add'
          ? hasAdditionalInvoice(row)
          : true,
    },
    { field: 'clientNpsBucket', label: 'NPS client', options: npsBucketOptions },
    { field: 'bloomerNpsBucket', label: 'NPS Bloomer', options: npsBucketOptions },
  ];
  if (amOptions.length > 0) {
    quickFilters.push({ field: 'accountManagerEmail', label: 'Tous les AM', options: amOptions });
  }

  const legend: DataGridV2LegendItem[] = [
    { label: 'Validé', tone: 'success' },
    { label: 'En attente client / Bloomer', tone: 'warning' },
    { label: 'À traiter (non soumis)', tone: 'danger', variant: 'striped' },
    { label: 'Non travaillé', tone: 'neutral' },
  ];

  const columns: DataGridV2Column<CraDashboardRow>[] = useMemo(() => {
    const cols: DataGridV2Column<CraDashboardRow>[] = [
      {
        field: 'bloomer',
        header: 'Bloomer',
        minWidth: 200,
        variant: 'avatar',
        searchText: (row) => `${row.bloomer} ${row.accountManager}`,
        avatar: (row) => ({
          initials: initials(row.bloomer),
          name: row.bloomer || '—',
          subtitle: row.accountManager ? `AM : ${row.accountManager}` : row.bloomerSocietyName || '—',
        }),
      },
    ];

    if (missionMode) {
      cols.push({
        field: 'activityMonth',
        header: 'Mois',
        width: 110,
        valueGetter: (row) => (row.activityMonth ? new Date(row.activityMonth).getTime() : 0),
        render: (row) => <strong>{fmtMonthShort(row.activityMonth)}</strong>,
      });
    } else {
      cols.push({
        field: 'client',
        header: 'Client / Mission',
        minWidth: 200,
        flex: 1,
        searchText: (row) => `${row.client} ${row.mission}`,
        render: (row) => (
          <TwoLine>
            <strong>{row.client || '—'}</strong>
            <span>{row.mission || '—'}</span>
          </TwoLine>
        ),
      });
    }

    cols.push(
      {
        field: 'daysEntered',
        header: 'Jours CRA',
        subHeader: 'saisis / prévus',
        width: 150,
        variant: 'progress',
        // Filtre numérique natif sur les jours saisis (au lieu du ratio) — TEC-3053.
        type: 'number',
        valueGetter: (row) => row.daysEntered,
        progress: (row) => ({ value: row.daysEntered, max: row.daysPlanned || row.daysEntered || 1 }),
      },
      {
        field: 'displayStatus',
        header: 'Statut CRA',
        minWidth: 140,
        variant: 'chip',
        valueOptions: craStatusOptions,
        chip: (row) => CRA_CHIP[row.displayStatus],
      },
      {
        field: 'bloomerSignature',
        header: 'Sig. Bloomer',
        width: 120,
        align: 'center',
        variant: 'chip',
        chip: (row) => ({ ...SIG_CHIP[row.bloomerSignature], dot: true }),
      },
      {
        field: 'clientSignature',
        header: 'Sig. Client',
        width: 120,
        align: 'center',
        variant: 'chip',
        chip: (row) => ({ ...SIG_CHIP[row.clientSignature], dot: true }),
      },
      {
        field: 'clientFact',
        header: 'Fact. Client',
        width: 130,
        valueOptions: factStatusOptions,
        render: (row) => (
          <FactCell fact={row.clientFact} amount={row.clientAmountHT} error={row.clientInvoiceLastError} />
        ),
      },
      {
        field: 'bloomerFact',
        header: 'Fact. Bloomer',
        width: 130,
        valueOptions: factStatusOptions,
        render: (row) => (
          <FactCell fact={row.bloomerFact} amount={row.bloomerAmountHT} error={row.bloomerInvoiceLastError} />
        ),
      },
      {
        field: 'nps',
        header: 'NPS',
        subHeader: 'client / Bloomer',
        width: 110,
        align: 'center',
        sortable: false,
        // Filtre numérique natif sur la meilleure note disponible — TEC-3053.
        type: 'number',
        valueGetter: (row) => row.clientScore ?? row.bloomerScore ?? null,
        render: (row) => (
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <NpsBadge score={row.clientScore} comment={row.clientComment} label="Client" />
            <NpsBadge score={row.bloomerScore} comment={row.bloomerComment} label="Bloomer" />
          </Stack>
        ),
      },
      {
        field: 'actions',
        header: 'Actions',
        width: 120,
        variant: 'actions',
        actions: (row) => [
          {
            label: 'Aperçu du CRA',
            icon: <VisibilityOutlinedIcon fontSize="small" />,
            disabled: !row.craURL,
            onClick: () => row.craURL && setPreview({ url: row.craURL, title: `CRA — ${row.bloomer}` }),
          },
          {
            label: 'Aperçu facture Bloomer',
            icon: <DescriptionOutlinedIcon fontSize="small" />,
            disabled: !row.invoiceURL,
            onClick: () =>
              row.invoiceURL && setPreview({ url: row.invoiceURL, title: `Facture Bloomer — ${row.bloomer}` }),
          },
          {
            label: 'Aperçu facture additionnelle',
            icon: <DescriptionOutlinedIcon fontSize="small" />,
            disabled: !row.additionalInvoiceURL,
            onClick: () =>
              row.additionalInvoiceURL &&
              setPreview({ url: row.additionalInvoiceURL, title: `Facture additionnelle — ${row.bloomer}` }),
          },
          {
            label: 'Forcer le CRA',
            icon: <BoltIcon fontSize="small" />,
            divider: true,
            disabled: !canForceCRA(row),
            onClick: () => onForceCRA?.(row),
          },
          {
            label: 'Relancer la signature client',
            icon: <EmailOutlinedIcon fontSize="small" />,
            disabled: !canRelaunchClient(row),
            onClick: () => onRelaunchClient?.(row),
          },
          {
            label: 'Relancer la signature Bloomer',
            icon: <EmailOutlinedIcon fontSize="small" />,
            disabled: !canRelaunchBloomer(row),
            onClick: () => onRelaunchBloomer?.(row),
          },
          {
            label: 'Relancer la facturation client',
            icon: <CreditCardOutlinedIcon fontSize="small" />,
            divider: true,
            disabled: !canRetryClientInvoice(row),
            onClick: () => onRetryClientInvoice?.(row),
          },
          {
            label: 'Relancer la facturation Bloomer',
            icon: <PaymentsOutlinedIcon fontSize="small" />,
            disabled: !canRetryBloomerInvoice(row),
            onClick: () => onRetryBloomerInvoice?.(row),
          },
          {
            label: 'Uploader la facture Bloomer',
            icon: <UploadFileOutlinedIcon fontSize="small" />,
            disabled: !canUploadBloomerInvoice(row),
            onClick: () => onUploadBloomerInvoice?.(row),
          },
          {
            label: 'Uploader la facture additionnelle',
            icon: <UploadFileOutlinedIcon fontSize="small" />,
            disabled: !canUploadAdditionalInvoice(row),
            onClick: () => onUploadAdditionalInvoice?.(row),
          },
          {
            label: "Consulter l'historique",
            icon: <HistoryOutlinedIcon fontSize="small" />,
            divider: true,
            disabled: !onViewHistory,
            onClick: () => onViewHistory?.(row),
          },
          {
            label: 'Voir le détail',
            icon: <OpenInNewIcon fontSize="small" />,
            onClick: () => setSelectedKey(selectionKey(row)),
          },
        ],
      },
    );

    return cols;
  }, [
    missionMode,
    onForceCRA,
    onRelaunchClient,
    onRelaunchBloomer,
    onRetryClientInvoice,
    onRetryBloomerInvoice,
    onUploadBloomerInvoice,
    onUploadAdditionalInvoice,
    onViewHistory,
  ]);

  const goToMonth = (offset: number) => {
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1);
    onMonthChange?.(next);
  };
  const subtitle = missionMode
    ? 'Suivi des activités, signatures et facturation de la mission, mois par mois.'
    : 'Suivi des activités, signatures et facturation du mois en cours.';

  return (
    <Page>
      <PageHeader id="titleCRADashboard" title="Tableau de bord CRA" subtitle={subtitle}>
        {!missionMode && onMonthChange ? (
          <MonthNav>
            <MuiIconButton size="small" aria-label="Mois précédent" onClick={() => goToMonth(-1)}>
              <ChevronLeftIcon fontSize="small" />
            </MuiIconButton>
            <DatePicker
              views={['year', 'month']}
              value={currentMonth}
              label="Mois"
              handleChange={() => undefined}
              onAccept={(date: DatePickerValue) => date && onMonthChange(date)}
            />
            <MuiIconButton size="small" aria-label="Mois suivant" onClick={() => goToMonth(1)}>
              <ChevronRightIcon fontSize="small" />
            </MuiIconButton>
          </MonthNav>
        ) : null}
      </PageHeader>

      {loading && rows.length === 0 ? (
        <LoaderGlobal waitingText="Chargement des CRA, ça va aller très vite..." />
      ) : (
        <>
          <KpiRow>
            <ObjectiveKpiCard
              accent="blue"
              icon={<CardItem variant="blue" size="small" emoji={<EventNoteOutlinedIcon fontSize="small" />} />}
              title={missionMode ? 'Mois suivis' : 'Missions actives'}
              valuePrimary={`${kpis.total}`}
              progressPercent={100}
              footer={focusValue ? 'Voir tout' : missionMode ? periodLabel : `ce mois · ${periodLabel}`}
              onClick={() => setFilterValues((prev) => ({ ...prev, [FOCUS_FIELD]: '' }))}
              data-testid="kpi-cra-actives"
            />
            <ObjectiveKpiCard
              accent="blue"
              icon={<CardItem variant="blue" size="small" emoji={<CheckCircleOutlinedIcon fontSize="small" />} />}
              title="CRA non validés"
              valuePrimary={`${kpis.notValidatedCount}`}
              valueSecondary={` / ${kpis.total}`}
              progressPercent={kpis.completionPct * 100}
              footer={`${fmtPct(kpis.completionPct)} validés · cliquer pour filtrer`}
              footerAccent={kpis.notValidatedCount > 0}
              onClick={() => toggleFocus('cra')}
              data-testid="kpi-cra-non-valides"
            />
            <ObjectiveKpiCard
              accent="pink"
              icon={<CardItem variant="pink" size="small" emoji={<WarningAmberOutlinedIcon fontSize="small" />} />}
              title="Alertes facturation"
              valuePrimary={`${kpis.invoiceActionCount}`}
              progressPercent={kpis.total ? (kpis.invoiceActionCount / kpis.total) * 100 : 0}
              footer="factures en erreur / à valider"
              footerAccent={kpis.invoiceActionCount > 0}
              onClick={() => toggleFocus('facture')}
              data-testid="kpi-cra-alertes"
            />
            <ObjectiveKpiCard
              accent="blue"
              icon={<CardItem variant="blue" size="small" emoji={<EventAvailableOutlinedIcon fontSize="small" />} />}
              title="Jours travaillés"
              valuePrimary={fmtDays(kpis.workedDaysTotal)}
              valueSecondary={` / ${fmtDays(kpis.plannedDaysTotal)}`}
              progressPercent={kpis.daysCompletionPct * 100}
              footer={`${fmtPct(kpis.daysCompletionPct)} des jours ouvrés saisis`}
              data-testid="kpi-cra-jours"
            />
            <ObjectiveKpiCard
              accent="pink"
              icon={
                <CardItem variant="pink" size="small" emoji={<SentimentSatisfiedOutlinedIcon fontSize="small" />} />
              }
              title="NPS client"
              valuePrimary={kpis.clientNps.scored ? `${kpis.clientNps.score}` : '—'}
              progressPercent={(kpis.clientNps.score + 100) / 2}
              footer={`${kpis.clientNps.promoters} promoteurs · ${kpis.clientNps.detractors} détracteurs`}
              footerAccent={kpis.clientNps.detractors > 0}
              onClick={() =>
                setFilterValues((prev) => ({
                  ...prev,
                  clientNpsBucket: prev.clientNpsBucket === 'detracteur' ? '' : 'detracteur',
                }))
              }
              data-testid="kpi-cra-nps-client"
            />
            <ObjectiveKpiCard
              accent="pink"
              icon={
                <CardItem variant="pink" size="small" emoji={<SentimentSatisfiedOutlinedIcon fontSize="small" />} />
              }
              title="NPS Bloomer"
              valuePrimary={kpis.bloomerNps.scored ? `${kpis.bloomerNps.score}` : '—'}
              progressPercent={(kpis.bloomerNps.score + 100) / 2}
              footer={`${kpis.bloomerNps.promoters} promoteurs · ${kpis.bloomerNps.detractors} détracteurs`}
              footerAccent={kpis.bloomerNps.detractors > 0}
              onClick={() =>
                setFilterValues((prev) => ({
                  ...prev,
                  bloomerNpsBucket: prev.bloomerNpsBucket === 'detracteur' ? '' : 'detracteur',
                }))
              }
              data-testid="kpi-cra-nps-bloomer"
            />
          </KpiRow>

          <SignatureBlock>
            <BlockTitle>Avancement signatures CRA — {periodLabel}</BlockTitle>
            <BarsRow>
              <ProgressBar
                label="Signatures Bloomer"
                value={kpis.bloomerSignedCount}
                total={kpis.total}
                tone="#2563EB"
              />
              <ProgressBar label="Signatures client" value={kpis.clientSignedCount} total={kpis.total} tone="#4D7236" />
              <ProgressBar
                label="CRA entièrement validés"
                value={kpis.validatedCount}
                total={kpis.total}
                tone="#C17A00"
              />
              <RelaunchActions>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={relaunchEligibleBloomers.length === 0}
                  onClick={() => onBulkRelaunch?.(relaunchEligibleBloomers)}
                >
                  📧 Relancer Bloomers ({kpis.relanceBloomerCount})
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  disabled={relaunchEligibleClients.length === 0}
                  onClick={() => onBulkRelaunch?.(relaunchEligibleClients)}
                >
                  📧 Relancer clients ({kpis.relanceClientCount})
                </Button>
              </RelaunchActions>
            </BarsRow>
          </SignatureBlock>

          <DataGridV2<CraDashboardRow>
            uniqueId={missionMode ? 'cra-dashboard-v2-mission' : 'cra-dashboard-v2-month'}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.rowId}
            loading={loading}
            loadingVariant="bar"
            search
            searchPlaceholder="Rechercher un Bloomer, client, mission…"
            tabs={tabs}
            quickFilters={quickFilters}
            quickFilterValues={filterValues}
            onQuickFilterValuesChange={setFilterValues}
            legend={legend}
            checkboxSelection
            getRowTone={(row) => (isAlert(row) ? 'danger' : undefined)}
            onRowClick={(row) => setSelectedKey(selectionKey(row))}
            bulkActions={(selectedRows, clear) => (
              <>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    onBulkRelaunch?.(selectedRows);
                    clear();
                  }}
                >
                  Relancer en masse
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    onExportSelection?.(selectedRows);
                    clear();
                  }}
                >
                  Export sélection
                </Button>
              </>
            )}
            pageSize={50}
            pagination
          />
        </>
      )}

      <Drawer anchor="right" open={!!selected} onClose={() => setSelectedKey(undefined)}>
        {selected ? (
          <CRADetailPanel
            row={selected}
            onClose={() => setSelectedKey(undefined)}
            onForceCRA={onForceCRA}
            onRelaunchClient={onRelaunchClient}
            onRelaunchBloomer={onRelaunchBloomer}
            onRetryClientInvoice={onRetryClientInvoice}
            onRetryBloomerInvoice={onRetryBloomerInvoice}
            onUploadBloomerInvoice={onUploadBloomerInvoice}
            onUploadAdditionalInvoice={onUploadAdditionalInvoice}
            onPreview={(url, title) => setPreview({ url, title })}
          />
        ) : null}
      </Drawer>

      <Modal
        open={!!preview}
        onClose={() => setPreview(undefined)}
        additionnalCSS={{ width: '822px', padding: '32px', height: '90%', border: 'none', borderRadius: '20px' }}
      >
        {preview ? <FileViewer width="100%" height="100%" url={preview.url} accessToken={token} /> : <div />}
      </Modal>
    </Page>
  );
};

// ── Invoice (fact.) cell ─────────────────────────────────────────────────────

const FactCell = ({ fact, amount, error }: { fact: FactState; amount?: number | null; error?: Maybe<string> }) => {
  const chip = FACT_CHIP[fact];
  return (
    <Stack spacing={0.4} alignItems="flex-start">
      <Stack direction="row" spacing={0.5} alignItems="center">
        <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
        {error ? (
          <Tooltip title={error} arrow>
            <ErrorOutlineIcon sx={{ fontSize: 15, color: '#B90A00', cursor: 'help' }} />
          </Tooltip>
        ) : null}
      </Stack>
      {amount ? <Amount>{formatEuro(amount)}</Amount> : null}
    </Stack>
  );
};

// ── NPS badge ────────────────────────────────────────────────────────────────

const NpsBadge = ({ score, comment, label }: { score?: number | null; comment?: Maybe<string>; label: string }) => {
  if (score === null || score === undefined) return <NpsEmpty title={`${label} : pas de note`}>—</NpsEmpty>;
  const tone = score >= 9 ? '#1E7B34' : score >= 7 ? '#C17A00' : '#B90A00';
  const tip = `${label} : ${score}/10${comment ? ` — « ${comment} »` : ''}`;
  return (
    <Tooltip title={tip} arrow>
      <NpsDot style={{ backgroundColor: tone }}>{score}</NpsDot>
    </Tooltip>
  );
};

export default ActivityDashboardV2;
export { ActivityDashboardV2 };

// ── Signature progress bar ───────────────────────────────────────────────────

const ProgressBar = ({ label, value, total, tone }: { label: string; value: number; total: number; tone: string }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <BarRoot>
      <BarHead>
        <BarLabel>{label}</BarLabel>
        <BarCount>
          {value}/{total}
        </BarCount>
      </BarHead>
      <BarTrack>
        <BarFill style={{ width: `${pct}%`, backgroundColor: tone }} />
      </BarTrack>
    </BarRoot>
  );
};

// ── Styled ───────────────────────────────────────────────────────────────────

const Page = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  // breathing room so the date picker's floating "Mois" label isn't clipped
  // by the top of the page.
  paddingTop: 20,
}));

const MonthNav = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}));

// KPI band laid out on a fixed 3-column grid (2 rows of 3 cards), collapsing to
// 2 columns then 1 on narrower viewports.
const KpiRow = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 16,
  [theme.breakpoints.down('lg')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const NpsDot = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: '50%',
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
}));

const NpsEmpty = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  fontSize: 12,
  color: theme.palette.neutral[40],
}));

const SignatureBlock = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.neutral.white,
  borderRadius: 12,
  boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}));

const BlockTitle = styled('div')(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
}));

const BarsRow = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: 24,
}));

const BarRoot = styled('div')(() => ({
  flex: '1 1 200px',
  minWidth: 180,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}));

const BarHead = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
}));

const BarLabel = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.neutral[70],
}));

const BarCount = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

const BarTrack = styled('div')(({ theme }) => ({
  width: '100%',
  height: 6,
  borderRadius: 999,
  backgroundColor: theme.palette.neutral[15],
  overflow: 'hidden',
}));

const BarFill = styled('div')(() => ({
  height: '100%',
  borderRadius: 999,
  transition: 'width 0.2s ease',
}));

const RelaunchActions = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  marginLeft: 'auto',
}));

const TwoLine = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  '& strong': { fontSize: 12.5, fontWeight: 700, color: '#212121' },
  '& span': {
    fontSize: 11,
    color: '#6B6B7E',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
}));

const Amount = styled('span')(() => ({
  fontSize: 11,
  fontWeight: 700,
  color: '#333342',
}));
